import {
  ActionIcon,
  Box,
  Button,
  Collapse,
  CopyButton,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconChevronRight,
  IconCoins,
  IconCopy,
  IconHeadset,
  IconHelpCircle,
  IconId,
  IconLink,
  IconLogout,
  IconPencil,
  IconSettings,
  IconStar,
  IconUser,
} from "@tabler/icons-react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarIconRailLink } from "@/components/layout/SidebarIconRail";
import { buildFacilitatorClientSignupUrl } from "@/shared/utils/facilitatorSignupUrl.util";
import { buildExpertClientSignupUrl } from "@/shared/utils/expertSignupUrl.util";
import {
  COLLABORATION_BUY_PATH,
  COLLABORATION_WALLET_PATH,
} from "@/shared/constants/collaborationWallet.const";

const CARD_STYLE = {
  borderRadius: 12,
  border: "1px solid rgba(255, 255, 255, 0.18)",
  backgroundColor: "rgba(2, 28, 54, 0.72)",
};

const MENU_ITEM_HOVER = {
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = "#ffc300";
    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = "transparent";
    e.currentTarget.style.color = "white";
  },
};

type AccountMenuItem = {
  label: string;
  icon: typeof IconPencil;
  onClick: () => void;
};

function AccountMenuList({ items }: { items: AccountMenuItem[] }) {
  return (
    <Stack gap={2}>
      {items.map((item) => (
        <UnstyledButton
          key={item.label}
          w="100%"
          py={10}
          px="sm"
          onClick={item.onClick}
          style={{
            borderRadius: 8,
            transition: "background-color 0.15s, color 0.15s",
            color: "#ffffff",
          }}
          {...MENU_ITEM_HOVER}
        >
          <Group justify="space-between" wrap="nowrap" c="inherit">
            <Group gap="sm" wrap="nowrap" c="inherit">
              <item.icon size={16} stroke={1.8} color="currentColor" />
              <Text fw={600} size="sm" c="inherit">
                {item.label}
              </Text>
            </Group>
            <IconChevronRight size={14} color="currentColor" style={{ opacity: 0.85 }} />
          </Group>
        </UnstyledButton>
      ))}
    </Stack>
  );
}

function CreditCoinsIllustration() {
  return (
    <svg width="56" height="42" viewBox="0 0 78 58" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="46" rx="16" ry="5" fill="rgba(0,0,0,0.25)" />
      <ellipse cx="52" cy="48" rx="18" ry="5" fill="rgba(0,0,0,0.25)" />
      {[0, 1, 2].map((i) => (
        <g key={`left-${i}`}>
          <ellipse cx="24" cy={38 - i * 8} rx="15" ry="5" fill="#1d4ed8" />
          <ellipse cx="24" cy={35 - i * 8} rx="15" ry="5" fill="#2563eb" />
          <ellipse cx="24" cy={32 - i * 8} rx="15" ry="4" fill="#3b82f6" />
        </g>
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={`right-${i}`}>
          <ellipse cx="52" cy={42 - i * 7} rx="17" ry="5" fill="#1d4ed8" />
          <ellipse cx="52" cy={39 - i * 7} rx="17" ry="5" fill="#2563eb" />
          <ellipse cx="52" cy={36 - i * 7} rx="17" ry="4" fill="#60a5fa" />
        </g>
      ))}
    </svg>
  );
}

type SidebarAccountRoutes = {
  account: string;
  editProfile: string;
  settings: string;
  wallet: string;
  walletBuy: string;
};

const USER_ACCOUNT_ROUTES: SidebarAccountRoutes = {
  account: "/account",
  editProfile: "/profile/edit",
  settings: "/settings",
  wallet: COLLABORATION_WALLET_PATH,
  walletBuy: COLLABORATION_BUY_PATH,
};

const FACILITATOR_ACCOUNT_ROUTES: SidebarAccountRoutes = {
  account: "/facilitator/account",
  editProfile: "/facilitator/account",
  settings: "/facilitator/settings",
  wallet: COLLABORATION_WALLET_PATH,
  walletBuy: COLLABORATION_BUY_PATH,
};

const ECOSYSTEM_HEAD_ACCOUNT_ROUTES: SidebarAccountRoutes = {
  account: "/ecosystem-head/account",
  editProfile: "/ecosystem-head/account",
  settings: "/ecosystem-head/settings",
  wallet: COLLABORATION_WALLET_PATH,
  walletBuy: COLLABORATION_BUY_PATH,
};

const EXPERT_ACCOUNT_ROUTES: SidebarAccountRoutes = {
  account: "/expert/account",
  editProfile: "/expert/company-profile/edit",
  settings: "/expert/settings",
  wallet: COLLABORATION_WALLET_PATH,
  walletBuy: COLLABORATION_BUY_PATH,
};

const EXPO_HEAD_ACCOUNT_ROUTES: SidebarAccountRoutes = {
  account: "/expo-head/account",
  editProfile: "/expo-head/account",
  settings: "/expo-head/settings",
  wallet: COLLABORATION_WALLET_PATH,
  walletBuy: COLLABORATION_BUY_PATH,
};

type SidebarDesktopFooterProps = {
  variant?: "user" | "facilitator" | "ecosystemHead" | "expert" | "expoHead";
  showCredits?: boolean;
  creditAvailable?: number | null;
  creditLoadFailed?: boolean;
  showReferenceId?: boolean;
  referenceId?: string | null;
  referenceIdLoading?: boolean;
  referenceIdLoadFailed?: boolean;
  referenceIdLabel?: string;
  companyName: string;
  gtid: string;
  companyStatus?: string;
  onLogout: () => void;
  /** Icon-only rail at the bottom when the desktop sidebar is collapsed. */
  collapsed?: boolean;
  /** Expo directory guests have no company profile — hide edit-profile actions. */
  hideProfileActions?: boolean;
};

export function SidebarDesktopFooter({
  variant = "user",
  showCredits = variant === "user",
  creditAvailable = null,
  creditLoadFailed = false,
  showReferenceId = false,
  referenceId = null,
  referenceIdLoading = false,
  referenceIdLoadFailed = false,
  referenceIdLabel = "Reference ID",
  companyName,
  gtid,
  companyStatus,
  onLogout,
  collapsed = false,
  hideProfileActions = false,
}: SidebarDesktopFooterProps) {
  const navigate = useNavigate();
  const routes =
    variant === "facilitator"
      ? FACILITATOR_ACCOUNT_ROUTES
      : variant === "ecosystemHead"
        ? ECOSYSTEM_HEAD_ACCOUNT_ROUTES
        : variant === "expert"
          ? EXPERT_ACCOUNT_ROUTES
          : variant === "expoHead"
            ? EXPO_HEAD_ACCOUNT_ROUTES
            : USER_ACCOUNT_ROUTES;
  const isDarkRail =
    variant === "facilitator" ||
    variant === "ecosystemHead" ||
    variant === "expert" ||
    variant === "expoHead";
  const buildClientSignupUrl =
    variant === "expert" ? buildExpertClientSignupUrl : buildFacilitatorClientSignupUrl;
  const [accountMenuOpened, { toggle: toggleAccountMenu, close: closeAccountMenu }] = useDisclosure(false);
  const accountToggleRef = useRef<HTMLButtonElement>(null);
  const accountMenuPanelRef = useRef<HTMLDivElement>(null);
  // Collapse sets aria-hidden while animating closed; move focus out first to avoid a11y warnings.
  const closeAccountMenuSafely = () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && accountMenuPanelRef.current?.contains(active)) {
      accountToggleRef.current?.focus({ preventScroll: true });
    }
    closeAccountMenu();
  };
  const accountMenuRef = useClickOutside(() => {
    if (accountMenuOpened) closeAccountMenuSafely();
  });

  const creditValueText = typeof creditAvailable === "number" ? String(creditAvailable) : null;
  const creditValueFontSize =
    creditValueText && creditValueText.length >= 7
      ? "22px"
      : creditValueText && creditValueText.length >= 5
        ? "26px"
        : "32px";

  const handleEditProfile = () => {
    if (variant === "user" && companyStatus === "UNDER_REVIEW") {
      sessionStorage.setItem(
        "gt:profileVerificationSummary",
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      navigate("/profile", {
        replace: true,
        state: { lockNoticeAt: Date.now() },
      });
      return;
    }
    if (variant === "expert" && companyStatus === "UNDER_REVIEW") {
      sessionStorage.setItem(
        "gt:profileVerificationSummary",
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      navigate("/expert/company-profile", {
        replace: true,
        state: { lockNoticeAt: Date.now() },
      });
      return;
    }
    navigate(routes.editProfile);
  };

  const handleOpenProfile = () => {
    closeAccountMenuSafely();
    navigate(routes.account);
  };

  const accountMenuItems: AccountMenuItem[] = [
    ...(variant !== "user" && variant !== "ecosystemHead"
      ? [
          {
            label: "My Profile",
            icon: IconUser,
            onClick: handleOpenProfile,
          } satisfies AccountMenuItem,
        ]
      : []),
    ...( !hideProfileActions && (variant === "user" || variant === "expert")
      ? [
          {
            label: "Edit Profile",
            icon: IconPencil,
            onClick: () => {
              closeAccountMenuSafely();
              handleEditProfile();
            },
          } satisfies AccountMenuItem,
        ]
      : []),
    ...(showCredits
      ? [
          {
            label: "Collaboration wallet",
            icon: IconCoins,
            onClick: () => {
              closeAccountMenuSafely();
              navigate(routes.wallet);
            },
          } satisfies AccountMenuItem,
        ]
      : []),
    {
      label: "Settings",
      icon: IconHelpCircle,
      onClick: () => {
        closeAccountMenuSafely();
        navigate(routes.settings);
      },
    },
    ...(hideProfileActions
      ? [
          {
            label: "My Profile",
            icon: IconUser,
            onClick: handleOpenProfile,
          } satisfies AccountMenuItem,
        ]
      : []),
    {
      label: "Logout",
      icon: IconLogout,
      onClick: () => {
        closeAccountMenuSafely();
        onLogout();
      },
    },
  ];

  if (collapsed) {
    const creditLabel =
      creditLoadFailed || creditAvailable === null
        ? "Collaborations"
        : `Collaborations (${creditAvailable} available)`;

    return (
      <>
        <Stack gap={4} align="stretch">
          {showReferenceId ? (
            referenceId ? (
              <>
                <CopyButton value={referenceId} timeout={2000}>
                  {({ copied, copy }) => (
                    <SidebarIconRailLink
                      icon={copied ? IconCheck : IconId}
                      label={`${referenceIdLabel} (${referenceId})`}
                      onClick={copy}
                      variant="dark"
                    />
                  )}
                </CopyButton>
                <CopyButton value={buildClientSignupUrl(referenceId)} timeout={2000}>
                  {({ copied, copy }) => (
                    <SidebarIconRailLink
                      icon={copied ? IconCheck : IconLink}
                      label="Copy signup link"
                      onClick={copy}
                      variant="dark"
                    />
                  )}
                </CopyButton>
              </>
            ) : (
              <SidebarIconRailLink
                icon={IconId}
                label={referenceIdLabel}
                onClick={() => {}}
                variant="dark"
              />
            )
          ) : null}
          {showCredits ? (
            <SidebarIconRailLink
              icon={IconCoins}
              label={creditLabel}
              onClick={() => navigate(routes.wallet)}
              variant={isDarkRail ? "dark" : undefined}
            />
          ) : null}
          <Menu position="right-start" withArrow shadow="md" width={220}>
            <Menu.Target>
              <Tooltip label={companyName || "Account"} position="right" withArrow>
                <UnstyledButton
                  aria-label="Account menu"
                  w="100%"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 44,
                    marginLeft: -8,
                    marginRight: -8,
                    borderRadius: 10,
                  }}
                >
                  <IconSettings size={22} stroke={1.8} color="rgba(255, 255, 255, 0.95)" />
                </UnstyledButton>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              {accountMenuItems.map((item) => (
                <Menu.Item key={item.label} leftSection={<item.icon size={16} />} onClick={item.onClick}>
                  {item.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <SidebarIconRailLink
            icon={IconHeadset}
            label="Contact Support"
            onClick={() => navigate(routes.settings)}
            {...(isDarkRail ? { variant: "dark" as const } : {})}
          />
          <SidebarIconRailLink
            icon={IconLogout}
            label="Logout"
            onClick={onLogout}
            {...(isDarkRail ? { variant: "dark" as const } : {})}
          />
        </Stack>
      </>
    );
  }

  return (
    <Stack gap="sm">
      {showReferenceId ? (
        <Box
          w="100%"
          p="md"
          style={{
            ...CARD_STYLE,
            textAlign: "left",
          }}
        >
          <Stack gap={10}>
            <Group gap={10} wrap="nowrap" align="center">
              <Box
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0, 0, 0, 0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconId size={14} color="#ffc300" stroke={1.8} />
              </Box>
              <Text c="white" size="sm" fw={700}>
                Reference ID
              </Text>
            </Group>

            <Group gap={6} wrap="nowrap" align="center" justify="space-between">
              {referenceIdLoadFailed ? (
                <Text size="sm" fw={800} c="white" lh={1} ff="monospace">
                  —
                </Text>
              ) : referenceIdLoading ? (
                <Loader size="sm" color="white" type="dots" />
              ) : (
                <>
                  <Text
                    size="sm"
                    fw={800}
                    c="white"
                    lh={1.2}
                    ff="monospace"
                    style={{ fontVariantNumeric: "tabular-nums", wordBreak: "break-all" }}
                  >
                    {referenceId ?? "—"}
                  </Text>
                  {referenceId ? (
                    <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <CopyButton value={referenceId} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copied" : "Copy reference ID"} withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray.0"
                              size="sm"
                              onClick={copy}
                              aria-label="Copy reference ID"
                            >
                              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      <CopyButton value={buildClientSignupUrl(referenceId)} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copied" : "Copy signup link"} withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray.0"
                              size="sm"
                              onClick={copy}
                              aria-label="Copy signup link"
                            >
                              {copied ? <IconCheck size={14} /> : <IconLink size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                  ) : null}
                </>
              )}
            </Group>

            <Text c="rgba(255, 255, 255, 0.72)" size="xs" lh={1.4}>
              Share this code or signup link so companies join your portfolio automatically.
            </Text>
          </Stack>
        </Box>
      ) : null}

      {showCredits ? (
      <Box
        role="button"
        tabIndex={0}
        aria-label="View collaborations"
        w="100%"
        p="md"
        onClick={() => navigate(routes.wallet)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(routes.wallet);
          }
        }}
        style={{
          ...CARD_STYLE,
          overflow: "hidden",
          textAlign: "left",
          cursor: "pointer",
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(2, 28, 54, 0.92)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = CARD_STYLE.backgroundColor;
        }}
      >
        <Stack gap={12}>
          <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
            <Group gap={10} wrap="nowrap" align="center" style={{ minWidth: 0, flex: 1 }}>
              <Box
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0, 0, 0, 0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconStar size={14} color="#ffc300" fill="#ffc300" stroke={1.5} />
              </Box>
              <Text c="white" size="sm" fw={700} lh={1.25} style={{ minWidth: 0 }}>
                Available collaboration opportunities
              </Text>
            </Group>

            <Box style={{ flexShrink: 0, paddingLeft: 8 }}>
              {creditLoadFailed ? (
                <Text
                  size={creditValueFontSize}
                  fw={800}
                  c="white"
                  lh={1}
                  style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                >
                  —
                </Text>
              ) : creditAvailable === null ? (
                <Loader size="sm" color="white" type="dots" />
              ) : (
                <Text
                  size={creditValueFontSize}
                  fw={800}
                  c="white"
                  lh={1}
                  style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                >
                  {creditAvailable}
                </Text>
              )}
            </Box>
          </Group>

          <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
            <Button
              size="xs"
              radius="md"
              variant="filled"
              style={{
                backgroundColor: "#ffc300",
                color: "#04305c",
                fontWeight: 800,
                flexShrink: 0,
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(routes.walletBuy);
              }}
            >
              Buy collaborations
            </Button>

            <Box
              style={{
                width: 56,
                height: 42,
                flexShrink: 0,
                overflow: "hidden",
                lineHeight: 0,
              }}
            >
              <CreditCoinsIllustration />
            </Box>
          </Group>
        </Stack>
      </Box>
      ) : null}

      <Box ref={accountMenuRef}>
        <Collapse in={accountMenuOpened}>
          <Box
            ref={accountMenuPanelRef}
            mb="xs"
            p="xs"
            style={{
              ...CARD_STYLE,
              backgroundColor: "rgba(2, 28, 54, 0.95)",
              boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.22)",
            }}
          >
            <AccountMenuList items={accountMenuItems} />
          </Box>
        </Collapse>

        <UnstyledButton
          ref={accountToggleRef}
          w="100%"
          p="md"
          onClick={toggleAccountMenu}
          aria-label="Open account menu"
          aria-expanded={accountMenuOpened}
          style={{
            ...CARD_STYLE,
            textAlign: "left",
            transition: "background-color 0.2s",
            backgroundColor: accountMenuOpened ? "rgba(2, 28, 54, 0.92)" : CARD_STYLE.backgroundColor,
          }}
          onMouseEnter={(e) => {
            if (!accountMenuOpened) {
              e.currentTarget.style.backgroundColor = "rgba(2, 28, 54, 0.92)";
            }
          }}
          onMouseLeave={(e) => {
            if (!accountMenuOpened) {
              e.currentTarget.style.backgroundColor = CARD_STYLE.backgroundColor;
            }
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm" fw={700} c="white" truncate>
                {companyName}
              </Text>
              <Text c="rgba(255, 255, 255, 0.72)" size="xs" truncate mt={2}>
                {gtid}
              </Text>
            </Box>

            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: "rgba(255, 255, 255, 0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconSettings size={18} color="white" />
            </Box>
          </Group>
        </UnstyledButton>
      </Box>

      <UnstyledButton
        w="100%"
        py="sm"
        px="xs"
        onClick={() => navigate(routes.settings)}
        style={{
          borderRadius: 8,
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconHeadset size={22} color="white" stroke={1.6} style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text fw={700} c="white" size="sm">
                Need Help?
              </Text>
              <Text size="xs" c="rgba(255, 255, 255, 0.78)" mt={2}>
                Contact Support
              </Text>
            </Box>
          </Group>
          <IconChevronRight size={16} color="rgba(255, 255, 255, 0.85)" style={{ flexShrink: 0 }} />
        </Group>
      </UnstyledButton>

    </Stack>
  );
}
